// Pages that belong to a role rather than a plan or a permission. The route
// enforces it (components/RouteRoleGuard), not just the nav: a deep link used
// to render the page for anyone, who then met a page full of refusals. The
// server refuses the data either way; this keeps the page from pretending.

/**
 * Organisation administration. The API behind each is admin-only. The old
 * addresses (/admin, /billing, /webhooks, /prompt-management, /audit-log,
 * /teams) are redirects into /organisation now (navigation/legacyRedirects).
 */
export const ADMIN_ONLY_PATHS: readonly string[] = ['/organisation'];

/** Under an admin-only path but open to anyone: Teams, which members may view. */
const OPEN_TO_MEMBERS: readonly string[] = ['/organisation/teams'];

/** Open to anyone, the page deciding where they go: Organisation itself sends members to Teams. */
const DECIDED_BY_PAGE: readonly string[] = ['/organisation'];

export type RoleGate = 'admin' | 'platform';

export interface RoleFacts {
  role?: string | null;
  isTenantAdmin?: boolean | null;
}

const under = (path: string, root: string) => path === root || path.startsWith(`${root}/`);

/** The role a route needs, or null when any signed-in user may open it. */
export function routeRoleFor(pathname: string): RoleGate | null {
  const path = pathname.toLowerCase();
  if (under(path, '/platform')) return 'platform';
  if (OPEN_TO_MEMBERS.some((root) => under(path, root))) return null;
  if (DECIDED_BY_PAGE.includes(path.replace(/\/+$/, ''))) return null;
  if (ADMIN_ONLY_PATHS.some((root) => under(path, root))) return 'admin';
  return null;
}

/** Admins of the organisation: the Admin and TenantAdmin roles, tenant admins of any role, and Stratium staff. */
export function isAdminUser(user: RoleFacts | null | undefined): boolean {
  if (!user) return false;
  return user.role === 'Admin' || user.role === 'TenantAdmin' || user.role === 'PlatformAdmin' || !!user.isTenantAdmin;
}

export function roleAllows(gate: RoleGate, user: RoleFacts | null | undefined): boolean {
  return gate === 'platform' ? user?.role === 'PlatformAdmin' : isAdminUser(user);
}
